import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, CircularProgress } from '@mui/material';
import { Description as QueryIcon } from '@mui/icons-material';
import { colors, fonts, spacing, borderRadius, tableStyles } from '../../constants';
import ProjectHeaderRow from './ProjectHeaderRow';
import QueryTableRow from './QueryTableRow';

/**
 * QueryTable Component
 * Main table component with header, filter row, and body
 */
const QueryTable = ({
  loading,
  total,
  paginatedProjects,
  groupedQueriesByProject,
  expandedProjects,
  projectsCatalog = [],
  tableAssignedFilter,
  assignedFilterOptions,
  onTableAssignedFilterChange,
  onToggleProject,
  onView,
  onEdit,
  onDelete,
  onReopen,
  onClose,
  reopeningId,
  closingId,
  selectedRegionCard,
  tableContainerRef,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
}) => {
  const tableHeaderStyles = {
    ...tableStyles.header,
    borderBottom: `2px solid ${colors.borderDefault}`,
    borderRight: `1px solid ${colors.borderDefault}`,
    '&:last-child': {
      borderRight: 'none',
    },
  };

  const tableBodyCellStyles = {
    ...tableStyles.cell,
    borderBottom: `2px solid ${colors.borderDefault}`,
    borderRight: `1px solid ${colors.borderDefault}`,
    '&:last-child': {
      borderRight: 'none',
    },
  };

  const tableRowStyles = {
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: colors.primaryTint,
    },
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: spacing.md,
          py: spacing.sm,
          background: '#1e293b',
          backgroundSize: '200% 200%',
          color: colors.textWhite,
          position: 'relative',
          overflow: 'hidden',
          height: '42px',
          minHeight: '42px',
          maxHeight: '42px',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: '#1e293b',
            filter: 'blur(50px)',
            opacity: 0.6,
            zIndex: 0,
          },
          '& > *': {
            position: 'relative',
            zIndex: 1,
          },
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: fonts.weightSemiBold,
            fontSize: fonts.fontSizeSm,
            letterSpacing: '0.02em',
            color: colors.textWhite,
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
          }}
        >
          Recent Queries {total > 0 && `(${total})`}
        </Typography>
      </Box>
      
      <TableContainer 
        sx={{ 
          border: `2px solid ${colors.borderDefault}`, 
          borderRadius: borderRadius.md, 
          borderTop: 'none',
          borderTopRightRadius: '0',
          borderTopLeftRadius: '0',
          borderBottom: 'none',
          borderBottomRightRadius: '0',
          borderBottomLeftRadius: '0',
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 400px)',
          minHeight: '400px',
          position: 'relative',
          width: '100%',
          '& .MuiTable-root': {
            width: '100%',
          },
        }}
      >
        {/* Sticky Filter Row - Inside scrolling container */}
        <Box
          sx={{ 
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            width: '100%',
            zIndex: 20,
            backgroundColor: colors.backgroundLight,
            borderBottom: `none`,
            py: { xs: spacing.xs, sm: spacing.sm },
            px: { xs: spacing.xs, sm: spacing.md },
            height: { xs: 'auto', sm: '48px' },
            minHeight: { xs: '44px', sm: '48px' },
            maxHeight: { xs: 'none', sm: '48px' },
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            boxSizing: 'border-box',
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: spacing.xs, sm: spacing.sm }, 
            borderTop: 'none', 
            width: '100%',
            flexWrap: { xs: 'nowrap', sm: 'nowrap' },
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: fonts.weightSemiBold, 
                color: colors.textPrimaryDark, 
                minWidth: { xs: '80px', sm: '100px' },
                fontSize: { xs: '0.75rem', sm: fonts.fontSizeSm },
                flexShrink: 0,
              }}
            >
              Assigned To:
            </Typography>
            <Box
              component="select"
              value={tableAssignedFilter}
              onChange={onTableAssignedFilterChange}
              sx={{
                padding: '6px 12px',
                borderRadius: borderRadius.input,
                border: `1.5px solid ${colors.borderDefault}`,
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                backgroundColor: colors.backgroundWhite,
                cursor: 'pointer',
                minWidth: { xs: '120px', sm: '200px' },
                width: { xs: '100%', sm: 'auto' },
                maxWidth: { xs: '100%', sm: '300px' },
                fontWeight: fonts.weightMedium,
                flex: { xs: 1, sm: '0 1 auto' },
                '&:focus': {
                  outline: 'none',
                  borderColor: colors.primary,
                },
              }}
            >
              <option value="">All</option>
              {assignedFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Box>
          </Box>
        </Box>
        
        <Table 
          sx={{ 
            minWidth: { xs: 800, sm: 960 },
            '& tbody tr:last-child td': {
              borderBottom: 'none !important',
            },
            '& tbody tr:last-child .MuiTableCell-root': {
              borderBottom: 'none !important',
            },
          }}
        >
          <TableHead
            sx={{
              position: 'sticky',
              top: '48px', // Height of filter row
              zIndex: 10,
              backgroundColor: '#1e293b',
              display: 'table-header-group',
              width: '100%',
              '& th': {
                backgroundColor: '#1e293b',
                position: 'relative',
                height: '42px',
                minHeight: '42px',
                maxHeight: '42px',
                verticalAlign: 'middle',
              },
              '& tr': {
                backgroundColor: '#1e293b',
                height: '42px',
                minHeight: '42px',
                maxHeight: '42px',
              },
            }}
          >
            <TableRow 
              sx={{ 
                backgroundColor: '#1e293b',
                '& th': {
                  backgroundColor: '#1e293b',
                },
              }}
            >
              <TableCell sx={{ ...tableHeaderStyles, color: '#ffffff' }}>Query Type</TableCell>
              <TableCell sx={{ ...tableHeaderStyles, color: '#ffffff' }}>Query ID</TableCell>
              <TableCell sx={{ ...tableHeaderStyles, color: '#ffffff' }}>Assigned To</TableCell>
              <TableCell sx={{ ...tableHeaderStyles, color: '#ffffff' }}>Status</TableCell>
              <TableCell sx={{ ...tableHeaderStyles, color: '#ffffff' }}>Last Updated</TableCell>
              <TableCell sx={{ ...tableHeaderStyles, color: '#ffffff',  width: '280px', minWidth: '280px' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              '& .MuiTableRow-root:last-of-type td': {
                borderBottom: 'none !important',
              },
              '& .MuiTableRow-root:last-of-type .MuiTableCell-root': {
                borderBottom: 'none !important',
              },
              '& .MuiTableRow-root:last-child td': {
                borderBottom: 'none !important',
              },
              '& .MuiTableRow-root:last-child .MuiTableCell-root': {
                borderBottom: 'none !important',
              },
            }}
          >
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'center', py: spacing.xl, borderBottom: 'none' }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : total === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'center', py: spacing.xl, borderBottom: 'none' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: spacing.sm,
                      py: spacing.xl,
                    }}
                  >
                    <QueryIcon sx={{ fontSize: '48px', color: colors.textTertiary, opacity: 0.3 }} />
                    <Typography
                      sx={{
                        color: colors.textSecondary,
                        fontSize: '0.9375rem',
                        fontWeight: fonts.weightMedium,
                      }}
                    >
                      {selectedRegionCard ? 'No projects in this region' : 'No projects found'}
                    </Typography>
                    <Typography
                      sx={{
                        color: colors.textTertiary,
                        fontSize: '0.8125rem',
                        fontWeight: fonts.weightRegular,
                      }}
                    >
                      {selectedRegionCard ? 'Try selecting a different region or adjusting filters' : 'Try adjusting your filters'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedProjects.map((projectName) => {
                const projectData = groupedQueriesByProject[projectName];
                if (!projectData) return null;
                
                const projectKey = projectName;
                const isExpanded = expandedProjects.has(projectKey);
                const queryCount = projectData.queries.length;
                
                return (
                  <React.Fragment key={projectKey}>
                    <ProjectHeaderRow
                      projectName={projectName}
                      projectsCatalog={projectsCatalog}
                      queryCount={queryCount}
                      isExpanded={isExpanded}
                      onToggle={() => onToggleProject(projectKey)}
                      tableBodyCellStyles={tableBodyCellStyles}
                    />
                    {isExpanded &&
                      projectData.queries.map((query) => (
                        <QueryTableRow
                          key={query._id}
                          query={query}
                          onView={onView}
                          onEdit={onEdit}
                          onDelete={onDelete}
                      onReopen={onReopen}
                      onClose={onClose}
                      reopeningId={reopeningId}
                      closingId={closingId}
                      tableBodyCellStyles={tableBodyCellStyles}
                      tableRowStyles={tableRowStyles}
                    />
                      ))}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          borderTop: `2px solid ${colors.borderDefault}`,
          borderBottom: `2px solid ${colors.borderDefault}`,
          backgroundColor: colors.backgroundWhite,
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            fontSize: fonts.fontSizeSm,
            color: colors.textPrimaryDark,
          },
          '& .MuiTablePagination-select': {
            cursor: 'pointer',
            '&:hover': {
              cursor: 'pointer',
            },
          },
          '& .MuiTablePagination-selectLabel': {
            cursor: 'default',
          },
          '& .MuiIconButton-root': {
            color: colors.textPrimaryDark,
            '&:hover': {
              backgroundColor: colors.primaryTint,
            },
          },
        }}
      />
    </>
  );
};

export default QueryTable;
